/**
 * @private
 * @desc Get radians from a degree angle
 * @param {Number} deg
 * @returns {number}
 */
function degToRad(deg) {
  return parseFloat(deg) * (Math.PI/180); // 180 = 360 deg / 2
}

/**
 * @private
 * @desc Ensure a latLng object has floats for coordinates
 * @param {Object} latLng {latitide, longitude}
 * @returns {Object}
 */
function ensureLatLngFloats(latLng) {
  latLng.latitude = parseFloat(latLng.latitude);
  latLng.longitude = parseFloat(latLng.longitude);
  return latLng;
}

/**
 * @name getKilometersBetweenPoints
 * @desc Pass in 2 latLng objects and get the distance between them in kilometers
 * @param {Object} point1 {latitude, longitude}
 * @param {Object} point2 {latitude, longitude}
 * @returns {number}
 * @memberof utils
 * @example
 * const chicago = {latitude:'41.8339042', longitude:'-88.0121516'};
 * const newYork = {latitude:'40.6976701', longitude:'-74.2598693'};
 * const km = utils.getKilometersBetweenPoints(chicago, newYork);
 * console.log(km); // 1155.0878807238932
 */
export function getKilometersBetweenPoints(point1, point2) {
  const R = 6371; // EARTH_RADIUS_IN_KM
  point1 = ensureLatLngFloats(point1);
  point2 = ensureLatLngFloats(point2);
  const dLat = degToRad(point2.latitude - point1.latitude);
  const dLon = degToRad(point2.longitude - point1.longitude);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(degToRad(point1.latitude)) * Math.cos(degToRad(point2.latitude)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in km
}

/**
 * @name getRegionForCoordinates
 * @desc Pass in a latLng and radius and get the deltas in order to get latLng for a search radius
 * @param {Object} coordinates {latitude, longitude}
 * @param {Number} [radius] defaults to 12 (kilometers)
 * @returns {Object}
 * @memberof utils
 * @example
 * const newYork = {latitude:'40.6976701', longitude:'-74.2598693'};
 * const kmRadius = 48;
 * const region = utils.getRegionForCoordinates(newYork, kmRadius);
 * console.log(region); // {latitude: 40.6976701, longitude: -74.2598693, latitudeDelta: 0.8639308855291576, longitudeDelta: 0.855108431071925}
 */
export function getRegionForCoordinates(coordinates, radius = 12) {
  coordinates = ensureLatLngFloats(coordinates);
  radius = parseInt(radius, 10);
  const ONE_KM_PER_LAT_DEGREE = (1 / 111.12);
  const latDelta = radius * ONE_KM_PER_LAT_DEGREE * 2;
  const longDelta = Math.cos(coordinates.latitude) * ONE_KM_PER_LAT_DEGREE * radius * 2;
  return {
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
    latitudeDelta: Math.abs(latDelta),
    longitudeDelta: Math.abs(longDelta)
  };
}

/**
 * @name getSearchRadiusForRegion
 * @desc Get the radius in km to use in a location search based on a region obj
 * @param {Object} region {latitude, longitude, latitudeDelta, longitudeDelta}
 * @returns {Number}
 * @memberof utils
 * @example
 * const newYork = {latitude:'40.6976701', longitude:'-74.2598693'};
 * const kmRadius = 48; // radius to retrieve delta for bounding box
 * const region = utils.getRegionForCoordinates(newYork, kmRadius);
 * const searchRadius = utils.getSearchRadiusForRegion(region); // searchRadius ensures locations outside circle kmRadius are included in map
 * console.log(searchRadius); // 60.052435462818856
 */
export function getSearchRadiusForRegion(region) {
  const latPointLeft = {latitude: region.latitude - (region.latitudeDelta * 0.5), longitude:region.longitude};
  const latPointRight = {latitude: region.latitude + (region.latitudeDelta * 0.5), longitude:region.longitude};
  const mapHorizontalDisplayRadius = getKilometersBetweenPoints(latPointLeft, latPointRight);

  const latPointTop = {latitude:region.latitude, longitude:region.longitude - (region.longitudeDelta * 0.5)};
  const latPointBottom = {latitude:region.latitude, longitude:region.longitude + (region.longitudeDelta * 0.5)};
  const mapVerticalDisplayRadius = getKilometersBetweenPoints(latPointTop, latPointBottom);

  return Math.sqrt(Math.pow(mapHorizontalDisplayRadius, 2) + Math.pow(mapVerticalDisplayRadius, 2)) / 2;
}
